import {useCallback, useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {chooseComparedPlan, openingComparedPlans, type ComparedPlans, type ComparisonPicker} from "./comparedPlans";
import type {PlanId} from "./planCatalogue";

/**
 * React adapter for the two-plan comparison's pickers (kitecorp/transferify-ui#4018): holds the pair, opens it
 * at the address's fragment and applies each pick. The rules are `comparedPlans.ts`'s.
 *
 * The fragment never reaches the server, so the server render and the first browser render both open on the
 * default pair, as hydration requires, and the effect moves to the fragment's pair right after.
 */
export function useComparedPlans(freePlanAnchorId: string | undefined): {
	compared: ComparedPlans;
	choose: (picker: ComparisonPicker, plan: PlanId) => void;
	/**
	 * A pick begins, when a picker takes focus or is pressed: the changes that follow count from the pair on
	 * screen now (`chooseComparedPlan`). A press starts afresh because a picker keeps focus after a pick, so a
	 * second pick in it would otherwise count from the pair before the first and undo that pick's swap.
	 */
	beginPick: () => void;
	/** The picker lost focus. */
	endPick: () => void;
} {
	const {hash} = useLocation();
	const [compared, setCompared] = useState<ComparedPlans>(() => openingComparedPlans("", freePlanAnchorId));
	const [pickFrom, setPickFrom] = useState<ComparedPlans | null>(null);
	useEffect(() => {
		setCompared(openingComparedPlans(hash, freePlanAnchorId));
	}, [hash, freePlanAnchorId]);
	const choose = useCallback((picker: ComparisonPicker, plan: PlanId) => {
		setCompared(current => chooseComparedPlan(pickFrom ?? current, picker, plan));
	}, [pickFrom]);
	const beginPick = useCallback(() => setPickFrom(compared), [compared]);
	const endPick = useCallback(() => setPickFrom(null), []);
	return {compared, choose, beginPick, endPick};
}
